import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { parseDateString } from "@/lib/utils/dateUtils";

/** Supported display formats for report canvas dates (ISO `YYYY-MM-DD` input). */
export const REPORT_DATE_FORMATS = [
  "monthDayYear",
  "dayAbbrevMonthYear",
] as const;

export type ReportDateFormat = (typeof REPORT_DATE_FORMATS)[number];

const REPORT_DATE_FORMAT_PATTERNS: Record<ReportDateFormat, string> = {
  monthDayYear: "MMMM d, yyyy",
  dayAbbrevMonthYear: "dd MMM yyyy",
};

/**
 * Formats an ISO date string for report preview and saved templates.
 * @param dateValue - `YYYY-MM-DD` date (local calendar day).
 * @param dateFormat - One of {@link REPORT_DATE_FORMATS}.
 * @returns Formatted label, or `null` when `dateValue` is missing or invalid.
 */
export const formatReportDate = (
  dateValue: string | null | undefined,
  dateFormat: ReportDateFormat,
): string | null => {
  if (dateValue == null || dateValue.trim() === "") {
    return null;
  }

  const date = parseDateString(dateValue.trim());
  if (date == null) {
    return null;
  }

  return format(date, REPORT_DATE_FORMAT_PATTERNS[dateFormat]);
};

export type ReportDateAlign = "left" | "right";

export interface ReportDateProps {
  dateValue: string;
  dateFormat: ReportDateFormat;
  align?: ReportDateAlign;
  className?: string;
}

/** Report date line: deterministic formatting and left or right alignment. */
export const ReportDate = ({
  dateValue,
  dateFormat,
  align = "right",
  className,
}: ReportDateProps) => {
  const label = formatReportDate(dateValue, dateFormat);
  if (label == null) {
    return null;
  }

  return (
    <p
      className={cn(
        "m-0 shrink-0 text-xs font-normal leading-4 tracking-[0.12px] text-muted-foreground",
        align === "left" ? "text-left" : "text-right",
        className,
      )}
    >
      {label}
    </p>
  );
};
